/**
 * SepaReturnPage — Callback après signature mandat SEPA via GoCardless.
 *
 * Route : /sepa/return?redirect_flow_id=...&souscription_id=...&sepa_plan_id=...
 *
 * Cette page complète le redirect flow côté backend, crée les 4 paiements
 * et affiche un récap du plan SEPA.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, AlertCircle, Calendar, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import OscopLogo from '../components/brand/OscopLogo';
import { useAuth } from '../contexts/AuthContext';
import { getDashboardRouteForRole } from '../utils/roleNavigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtEUR(cents) {
  return ((cents || 0) / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
}

const STATUS_LABELS = {
  scheduled: 'Planifiée', pending: 'En cours', submitted: 'Soumise',
  paid: 'Payée', failed: 'Échouée', cancelled: 'Annulée',
  creation_failed: 'Échec création',
};

export default function SepaReturnPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState({ status: 'loading', error: null, data: null });

  useEffect(() => {
    const redirectFlowId = params.get('stripe_session_id') || params.get('redirect_flow_id');
    let souscriptionId = params.get('souscription_id');
    if (!souscriptionId) {
      try {
        const cached = JSON.parse(localStorage.getItem('sepa_pending') || '{}');
        souscriptionId = cached.souscription_id;
      } catch { /* noop */ }
    }
    if (params.get('sepa_setup') === 'cancelled') {
      setState({ status: 'error', error: 'Signature du mandat SEPA annulée', data: null });
      return;
    }
    if (!redirectFlowId || !souscriptionId) {
      setState({ status: 'error', error: 'Paramètres de retour manquants', data: null });
      return;
    }
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.post(
          `${API}/sepa-stripe/redirect-flow/complete`,
          { stripe_session_id: redirectFlowId, souscription_id: souscriptionId },
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
        setState({ status: 'success', error: null, data: { ...res.data, souscription_id: souscriptionId } });
        try { localStorage.removeItem('sepa_pending'); } catch { /* noop */ }
      } catch (e) {
        setState({
          status: 'error',
          error: e.response?.data?.detail || e.message || 'Erreur de finalisation',
          data: null,
        });
      }
    })();
  }, [params]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-[#DEA900]/5 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#0a3d62] to-[#0c4a78] p-6 mb-6 shadow-[0_20px_60px_-15px_rgba(10,61,98,0.45)]">
          <div className="absolute inset-0 opacity-[0.08] mix-blend-overlay" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Cg fill='%23DEA900' fill-opacity='0.5'%3E%3Ccircle cx='1' cy='1' r='1'/%3E%3C/g%3E%3C/svg%3E\")" }} />
          <div className="absolute -right-16 -top-16 w-48 h-48 rounded-full bg-[#DEA900]/15 blur-3xl" />
          <div className="relative flex items-center gap-4">
            <div className="h-14 w-14 rounded-xl bg-[#DEA900] flex items-center justify-center shadow-lg shadow-[#DEA900]/30 ring-1 ring-[#f5cc3e]/40">
              <OscopLogo size={36} variant="on-navy" />
            </div>
            <div>
              <div className="text-[10px] font-bold tracking-[0.18em] text-[#f5cc3e] uppercase">O'SCOP · Mandat SEPA</div>
              <h1 className="text-2xl font-bold text-white tracking-tight mt-1">Finalisation du prélèvement</h1>
              <p className="text-white/70 text-sm mt-0.5">Retour de signature Stripe SEPA</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-[0_12px_40px_-12px_rgba(10,61,98,0.18)]">
          {state.status === 'loading' && (
            <div className="text-center py-12" data-testid="sepa-return-loading">
              <Loader2 className="h-12 w-12 animate-spin text-[#DEA900] mx-auto mb-4" />
              <p className="text-[#0a3d62] font-semibold text-lg">Finalisation de votre mandat SEPA…</p>
              <p className="text-slate-500 text-sm mt-1">Création des 4 prélèvements en cours, merci de patienter.</p>
            </div>
          )}

          {state.status === 'success' && state.data && (
            <div data-testid="sepa-return-success">
              <div className="flex items-center justify-center mb-5">
                <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="h-8 w-8 text-emerald-600" />
                </div>
              </div>
              <h2 className="text-xl font-bold text-center text-[#0a3d62] mb-1">Mandat SEPA enregistré avec succès</h2>
              <p className="text-center text-slate-500 mb-6">Vos 4 prélèvements ont été planifiés. Vous recevrez une notification avant chaque échéance.</p>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Réf. souscription</p>
                    <p className="font-mono font-semibold text-[#0a3d62] mt-0.5">{state.data.souscription_id}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Mandat SEPA</p>
                    <p className="font-mono font-semibold text-[#0a3d62] mt-0.5">{state.data.mandate_id}</p>
                  </div>
                </div>
              </div>

              <div className="border border-slate-200 rounded-lg overflow-hidden mb-5">
                <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-[#DEA900]" />
                  <span className="text-[11px] uppercase tracking-wider text-[#0a3d62] font-bold">Calendrier des prélèvements</span>
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {(state.data.installments || []).map((inst) => (
                      <tr key={inst.index} className="border-b border-slate-100 last:border-b-0">
                        <td className="px-4 py-2 font-bold text-[#0a3d62]">#{inst.index}</td>
                        <td className="px-4 py-2 text-slate-700">{fmtDate(inst.charge_date)}</td>
                        <td className="px-4 py-2 text-right font-mono tabular-nums text-[#0a3d62]">{fmtEUR(inst.amount_cents)}</td>
                        <td className="px-4 py-2 text-center text-[11px] text-slate-600">{STATUS_LABELS[inst.status] || inst.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Button
                onClick={() => navigate(getDashboardRouteForRole(user?.role))}
                className="w-full h-12 bg-[#0a3d62] hover:bg-[#0c4a78] text-white font-semibold"
                data-testid="sepa-return-back-btn"
              >
                Retour au tableau de bord <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          )}

          {state.status === 'error' && (
            <div className="text-center py-8" data-testid="sepa-return-error">
              <div className="h-16 w-16 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="h-8 w-8 text-rose-600" />
              </div>
              <h2 className="text-xl font-bold text-rose-700 mb-2">Erreur lors de la finalisation</h2>
              <p className="text-slate-700 mb-4">{state.error}</p>
              <Button onClick={() => navigate(getDashboardRouteForRole(user?.role))} variant="outline" data-testid="sepa-return-retry-btn">
                Retour au tableau de bord
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
